import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
    X, Search, FileVideo, FileImage, FileText, FileAudio, File
} from 'lucide-react';
import API_URL from '../api';
import { useToast } from '../contexts/ToastContext';
import './LibrarySelectModal.css';

const LibrarySelectModal = ({ isOpen, allowedType, onSelect, onClose }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('all');

    const { error: toastError } = useToast();

    const fetchLibraryItems = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/library`);
            setItems(response.data);
        } catch (err) {
            console.error('Failed to fetch library items:', err);
            toastError('Не вдалося завантажити медіабібліотеку');
        } finally {
            setLoading(false);
        }
    }, [toastError]);

    // Map lesson step type to library type
    // Course step types: ['video', 'text', 'pdf', 'Quiz', ...]
    // Library types: ['video', 'image', 'pdf', 'document', 'audio', 'other']
    useEffect(() => {
        if (isOpen) {
            fetchLibraryItems();
            
            // Set default tab based on allowedType
            if (allowedType) {
                if (allowedType === 'video') setActiveTab('video');
                else if (allowedType === 'pdf') setActiveTab('pdf');
                else if (allowedType === 'image') setActiveTab('image');
                else setActiveTab('all');
            } else {
                setActiveTab('all');
            }
        }
    }, [isOpen, allowedType, fetchLibraryItems]);

    if (!isOpen) return null;

    const formatBytes = (bytes, decimals = 2) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    const renderFileIcon = (type) => {
        switch (type) {
            case 'video':
                return <FileVideo className="fallback-icon text-purple" />;
            case 'image':
                return <FileImage className="fallback-icon text-green" />;
            case 'pdf':
                return <FileText className="fallback-icon text-red" style={{ color: '#ef4444' }} />;
            case 'document':
                return <FileText className="fallback-icon text-yellow" style={{ color: '#eab308' }} />;
            case 'audio':
                return <FileAudio className="fallback-icon text-blue" />;
            default:
                return <File className="fallback-icon" />;
        }
    };

    // Filter items locally
    const filteredItems = items.filter(item => {
        const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                             item.originalName.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesTab = activeTab === 'all' || item.type === activeTab;
        return matchesSearch && matchesTab;
    });

    return (
        <div className="lib-select-modal-overlay" onClick={onClose}>
            <div className="lib-select-modal" onClick={(e) => e.stopPropagation()}>
                <div className="lib-select-modal-header">
                    <h3>📂 Вибрати файл з Бібліотеки</h3>
                    <button className="close-modal-btn" onClick={onClose}>
                        <X />
                    </button>
                </div>

                {/* Toolbar */}
                <div className="lib-select-modal-toolbar">
                    <div className="lib-select-tabs">
                        <button 
                            className={`lib-select-tab ${activeTab === 'all' ? 'active' : ''}`}
                            onClick={() => setActiveTab('all')}
                        >
                            Усі
                        </button>
                        <button 
                            className={`lib-select-tab ${activeTab === 'video' ? 'active' : ''}`}
                            onClick={() => setActiveTab('video')}
                        >
                            Відео
                        </button>
                        <button 
                            className={`lib-select-tab ${activeTab === 'image' ? 'active' : ''}`}
                            onClick={() => setActiveTab('image')}
                        >
                            Зображення
                        </button>
                        <button 
                            className={`lib-select-tab ${activeTab === 'pdf' ? 'active' : ''}`}
                            onClick={() => setActiveTab('pdf')}
                        >
                            PDF
                        </button>
                        <button 
                            className={`lib-select-tab ${activeTab === 'document' ? 'active' : ''}`}
                            onClick={() => setActiveTab('document')}
                        >
                            Документи
                        </button>
                    </div>

                    <div className="search-wrapper" style={{ margin: 0 }}>
                        <Search className="search-icon" size={16} />
                        <input 
                            type="text" 
                            placeholder="Пошук..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                            style={{ width: '200px', padding: '0.45rem 1rem 0.45rem 2.2rem', fontSize: '0.88rem' }}
                        />
                    </div>
                </div>

                {/* Body */}
                <div className="lib-select-modal-body">
                    {loading ? (
                        <div className="lib-select-empty">
                            <h4>Завантаження медіафайлів...</h4>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="lib-select-empty">
                            <h4>Нічого не знайдено</h4>
                            <p>Завантажте медіафайли у розділі «Бібліотека» перед додаванням у конструктор.</p>
                        </div>
                    ) : (
                        <div className="lib-select-list">
                            {filteredItems.map(item => (
                                <div 
                                    className="lib-select-item" 
                                    key={item._id}
                                    onClick={() => onSelect(item.url)}
                                >
                                    <div className="lib-select-item-left">
                                        <div className="lib-select-item-icon">
                                            {renderFileIcon(item.type)}
                                        </div>
                                        <div className="lib-select-item-info">
                                            <h4 className="lib-select-item-title" title={item.title}>
                                                {item.title}
                                            </h4>
                                            <span className="lib-select-item-meta">
                                                {item.type} • {formatBytes(item.size)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="lib-select-item-right">
                                        <button className="lib-select-btn">
                                            Вибрати
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LibrarySelectModal;
